<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-account me-2"></i>{{ $t('page.users_roles', 'Users & Roles') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button v-if="tab === 'users'" class="hms-btn hms-btn-primary" @click="openAddUser">
            <i class="mdi mdi-account-plus me-1"></i>Add User
          </button>
          <button v-if="tab === 'roles'" class="hms-btn hms-btn-primary" @click="openAddRole">
            <i class="mdi mdi-shield-plus me-1"></i>Add Role
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="tab='users'; filterStatus=''" :class="{ selected: tab==='users' && !filterStatus }">
          <div class="hms-stat-num">{{ users.length }}</div>
          <div class="hms-stat-lbl">Total Users</div>
        </div>
        <div class="hms-stat" @click="tab='users'; filterStatus='active'" :class="{ selected: tab==='users' && filterStatus==='active' }">
          <div class="hms-stat-num hms-stat-green">{{ activeCount }}</div>
          <div class="hms-stat-lbl">Active</div>
        </div>
        <div class="hms-stat" @click="tab='users'; filterStatus='inactive'" :class="{ selected: tab==='users' && filterStatus==='inactive' }">
          <div class="hms-stat-num hms-stat-amber">{{ inactiveCount }}</div>
          <div class="hms-stat-lbl">Inactive</div>
        </div>
        <div class="hms-stat" @click="tab='users'">
          <div class="hms-stat-num hms-stat-info">{{ mustChangeCount }}</div>
          <div class="hms-stat-lbl">Must Change Pwd</div>
        </div>
        <div class="hms-stat" @click="tab='roles'" :class="{ selected: tab==='roles' }">
          <div class="hms-stat-num hms-stat-purple">{{ roles.length }}</div>
          <div class="hms-stat-lbl">Roles</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='users' }" @click="tab='users'">
          <i class="mdi mdi-account-group me-1"></i>Users
        </button>
        <button class="hms-tab" :class="{ active: tab==='roles' }" @click="tab='roles'">
          <i class="mdi mdi-shield-account me-1"></i>Roles
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ USERS TAB ============ -->
      <div v-else-if="tab === 'users'" class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search name, username, email, phone..." />
          </div>
          <select class="hms-input hms-filter" v-model="filterBranch">
            <option value="">All facilities</option>
            <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="filterRole">
            <option value="">All roles</option>
            <option v-for="r in roles" :key="r.role_id" :value="r.role_id">{{ r.role_name }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="filterStatus">
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredUsers.length }} users</span>
        </div>

        <div v-if="filteredUsers.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>User</th>
              <th>Username</th>
              <th>Role</th>
              <th>Facility</th>
              <th>Contact</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="u in filteredUsers" :key="u.user_id">
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="hms-avatar">{{ initials(u) }}</div>
                    <div>
                      <div class="hms-text fw-semibold">{{ fullName(u) || '—' }}</div>
                      <div class="hms-muted-sm" v-if="u.must_change_password == 1">
                        <i class="mdi mdi-key-alert text-warning"></i> Must change password
                      </div>
                    </div>
                  </div>
                </td>
                <td class="hms-mono">{{ u.username }}</td>
                <td><span class="hms-badge hms-badge-info">{{ u.role_name || '—' }}</span></td>
                <td>{{ u.branch_name || '—' }}</td>
                <td>
                  <div v-if="u.email" class="hms-muted-sm"><i class="mdi mdi-email-outline"></i> {{ u.email }}</div>
                  <div v-if="u.phone" class="hms-muted-sm"><i class="mdi mdi-phone-outline"></i> {{ u.phone }}</div>
                </td>
                <td>
                  <span class="hms-muted-sm">{{ u.last_login ? formatDateTime(u.last_login) : 'Never' }}</span>
                </td>
                <td>
                  <span v-if="u.is_locked == 1" class="hms-badge hms-badge-danger" :title="'Locked until ' + formatDateTime(u.locked_until) + ' after failed sign-in attempts'"><i class="mdi mdi-lock"></i> Locked</span>
                  <span v-else class="hms-badge" :class="u.is_active == 1 ? 'hms-badge-success' : 'hms-badge-neutral'">{{ u.is_active == 1 ? 'Active' : 'Inactive' }}</span>
                </td>
                <td class="d-flex gap-1 flex-wrap">
                  <button v-if="u.is_locked == 1" class="hms-btn hms-btn-sm hms-btn-green" @click="unlockUser(u)" title="Unlock account"><i class="mdi mdi-lock-open-variant"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openDetail(u)" title="View"><i class="mdi mdi-eye"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEditUser(u)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-warning" @click="confirmReset(u)" title="Reset password"><i class="mdi mdi-key-variant"></i></button>
                  <button v-if="u.mfa_enabled == 1" class="hms-btn hms-btn-sm hms-btn-purple" @click="confirmDisableMfa(u)" title="Disable MFA (admin override)"><i class="mdi mdi-shield-off"></i></button>
                  <button v-if="u.is_active == 1" class="hms-btn hms-btn-sm hms-btn-danger" @click="confirmDeactivate(u)" :disabled="u.user_id == myUserId" title="Deactivate"><i class="mdi mdi-account-off"></i></button>
                  <button v-else class="hms-btn hms-btn-sm hms-btn-green" @click="activate(u)" title="Activate"><i class="mdi mdi-account-check"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-account-search"></i>No users match.</div>
      </div>

      <!-- ============ ROLES TAB ============ -->
      <div v-else-if="tab === 'roles'" class="hms-card">
        <div class="hms-roles-grid" v-if="roles.length">
          <div v-for="r in roles" :key="r.role_id" class="hms-role-card">
            <div class="hms-role-head">
              <i class="mdi mdi-shield-edit-outline"></i>
              <span class="hms-role-name">{{ r.role_name }}</span>
            </div>
            <div class="hms-role-code hms-mono">{{ r.role_code }}</div>
            <div class="hms-role-desc">{{ r.description || '—' }}</div>
            <div class="hms-role-foot">
              <span class="hms-muted-sm"><i class="mdi mdi-account-multiple"></i> {{ r.user_count || 0 }} users</span>
              <div class="d-flex gap-1">
                <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEditRole(r)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                <button class="hms-btn hms-btn-sm hms-btn-danger" @click="confirmDeleteRole(r)" :disabled="(r.user_count || 0) > 0" title="Delete"><i class="mdi mdi-trash-can"></i></button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-shield-plus-outline"></i>No roles yet — click "Add Role" to create your hospital's roles and pick the menus each one can access.</div>
      </div>

      <!-- ============ ADD/EDIT USER MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showUserForm" @click.self="showUserForm=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editingUser ? 'mdi-account-edit' : 'mdi-account-plus'"></i> {{ editingUser ? 'Edit User' : 'Add User' }}</span>
            <button class="hms-modal-close" @click="showUserForm=false">&times;</button>
          </div>
          <div v-if="userFormError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ userFormError }}</div>

          <div class="hms-section-label">PROFILE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">First Name *</label>
              <input class="hms-input" v-model="userForm.first_name" />
            </div>
            <div class="col-md-6"><label class="hms-label">Last Name *</label>
              <input class="hms-input" v-model="userForm.last_name" />
            </div>
            <div class="col-md-6"><label class="hms-label">Email *</label>
              <input type="email" class="hms-input" v-model="userForm.email" placeholder="jane.doe@facility.co.ke" />
              <div class="hms-muted-sm mt-1">
                <i class="mdi mdi-email-fast-outline"></i>
                Login credentials will be emailed here after the account is created.
              </div>
            </div>
            <div class="col-md-6"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="userForm.phone" />
            </div>
          </div>

          <div class="hms-section-label">CREDENTIALS & ACCESS</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6" v-if="!editingUser"><label class="hms-label">Username *</label>
              <input class="hms-input" v-model="userForm.username" placeholder="e.g. jane.doe" />
            </div>
            <div class="col-md-6"><label class="hms-label">Role *</label>
              <select class="hms-input" v-model="userForm.role_id">
                <option value="">— Select role —</option>
                <option v-for="r in roles" :key="r.role_id" :value="r.role_id">{{ r.role_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Facility Name *</label>
              <select class="hms-input" v-model="userForm.branch_id">
                <option value="">— Select facility —</option>
                <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Linked Staff (optional)</label>
              <select class="hms-input" v-model="userForm.staff_id">
                <option value="">— None —</option>
                <option v-for="s in staffList" :key="s.staff_id" :value="s.staff_id">{{ s.first_name }} {{ s.last_name }} ({{ s.position }})</option>
              </select>
            </div>
          </div>

          <div v-if="editingUser" class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="userForm.is_active">
                <option :value="1">Active</option>
                <option :value="0">Inactive</option>
              </select>
            </div>
          </div>

          <div v-if="!editingUser" class="hms-info-banner">
            <i class="mdi mdi-information-outline me-1"></i>
            A temporary password will be generated and shown once after creation. The user must change it on first login.
          </div>

          <button class="hms-btn hms-btn-primary w-100 mt-2" @click="saveUser" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editingUser ? 'Save Changes' : 'Create User') }}
          </button>
        </div>
      </div>

      <!-- ============ ADD/EDIT ROLE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showRoleForm" @click.self="showRoleForm=false">
        <div class="hms-modal hms-modal-xl">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editingRole ? 'mdi-shield-edit' : 'mdi-shield-plus'"></i> {{ editingRole ? 'Edit Role' : 'Add Custom Role' }}</span>
            <button class="hms-modal-close" @click="showRoleForm=false">&times;</button>
          </div>
          <div v-if="roleFormError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ roleFormError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Role Name *</label>
              <input class="hms-input" v-model="roleForm.role_name" placeholder="e.g. Senior Consultant" />
            </div>
            <div class="col-12"><label class="hms-label">Role Code *</label>
              <input class="hms-input" v-model="roleForm.role_code" :disabled="editingRole" placeholder="senior_consultant" />
              <div class="hms-muted-sm mt-1" v-if="!editingRole">Lowercase, underscores only — used as a stable identifier.</div>
            </div>
            <div class="col-12"><label class="hms-label">Description</label>
              <textarea class="hms-input" rows="3" v-model="roleForm.description"></textarea>
            </div>
            <div class="col-12"><label class="hms-label">Scope of visibility</label>
              <div class="hms-muted-sm mb-2">Tick every level this role should be able to see. Roles often span more than one — e.g. a Med Sup views Facility AND Department; a County CEC views County + Sub-county + Facility.</div>
              <div class="hms-scope-grid">
                <label v-for="v in scopeOptions" :key="v.value" class="hms-scope-item"
                       :class="{ active: (roleForm.view_levels || []).indexOf(v.value) !== -1 }">
                  <input type="checkbox" :value="v.value" v-model="roleForm.view_levels" />
                  <span>
                    <strong>{{ v.label }}</strong>
                    <span class="hms-muted-sm d-block">{{ v.hint }}</span>
                  </span>
                </label>
              </div>
            </div>
            <div class="col-md-6" v-if="scopeHas('county') || scopeHas('sub_county')">
              <label class="hms-label">Scope county</label>
              <select v-model.number="roleForm.scope_county_id" class="hms-input">
                <option :value="null">— Any —</option>
                <option v-for="c in counties" :key="c.county_id" :value="c.county_id">{{ c.county_name }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="scopeHas('sub_county')">
              <label class="hms-label">Scope sub-county</label>
              <select v-model.number="roleForm.scope_sub_county_id" class="hms-input">
                <option :value="null">— Any —</option>
                <option v-for="s in subCounties" :key="s.sub_county_id" :value="s.sub_county_id">{{ s.sub_county_name }}</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Module access — rights</label>
              <div class="hms-muted-sm mb-2">Every menu is a right, grouped by module. Tick individual menus, or use a group's Select All to grant the whole module. Leave ALL unticked to allow every module.</div>

              <!-- Quick actions bar -->
              <div class="hms-rights-actions">
                <div>
                  <button type="button" class="hms-btn hms-btn-sm hms-btn-blue me-2" @click="selectAllRights">
                    <i class="mdi mdi-checkbox-multiple-marked me-1"></i>Select All
                  </button>
                  <button type="button" class="hms-btn hms-btn-sm hms-btn-blue" @click="deselectAllRights">
                    <i class="mdi mdi-checkbox-multiple-blank-outline me-1"></i>Deselect All
                  </button>
                </div>
                <span class="hms-muted-sm">{{ (roleForm.rights || []).length }} selected</span>
              </div>

              <!-- Group cards grid -->
              <div class="hms-rights-grid">
                <div v-for="g in rightsCatalog" :key="g.group_id" class="hms-rights-card">
                  <div class="hms-rights-card-head">
                    <span class="hms-rights-card-name"><i :class="g.icon"></i> {{ g.name }}</span>
                    <span class="hms-rights-card-badge">{{ selectedInGroup(g) }}/{{ g.rights.length }}</span>
                  </div>
                  <div class="hms-rights-card-body">
                    <label class="hms-rights-selectall">
                      <input type="checkbox" :checked="isGroupFull(g)" @change="toggleGroup(g)" />
                      <span>Select All</span>
                    </label>
                    <label v-for="rt in g.rights" :key="rt.right_id" class="hms-rights-item"
                           :class="{ active: (roleForm.rights || []).indexOf(Number(rt.right_id)) !== -1 }">
                      <input type="checkbox" :value="Number(rt.right_id)" v-model="roleForm.rights" />
                      <span>{{ rt.name }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Selected summary -->
              <div class="hms-rights-summary">
                <strong>Selected rights:</strong>
                <span class="hms-badge hms-badge-info">{{ (roleForm.rights || []).length }}</span>
                <span v-if="!(roleForm.rights || []).length" class="hms-muted-sm">none — role will see every module</span>
                <span v-for="rt in selectedRightObjects" :key="rt.right_id" class="hms-rights-chip">
                  {{ rt.name }}<i class="mdi mdi-close" @click="removeRight(rt)"></i>
                </span>
              </div>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveRole" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editingRole ? 'Save Changes' : 'Create Role') }}
          </button>
        </div>
      </div>

      <!-- ============ DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDetail" @click.self="showDetail=false">
        <div class="hms-modal" v-if="detail">
          <div class="hms-modal-title">
            <span class="d-flex align-items-center gap-2">
              <div class="hms-avatar hms-avatar-lg">{{ initials(detail) }}</div>
              <div>
                <div>{{ fullName(detail) }}</div>
                <div class="hms-muted-sm hms-mono">@{{ detail.username }}</div>
              </div>
            </span>
            <button class="hms-modal-close" @click="showDetail=false">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Role</span><span><span class="hms-badge hms-badge-info">{{ detail.role_name || '—' }}</span></span></div>
            <div><span class="hms-kv-key">Facility</span><span>{{ detail.branch_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Email</span><span>{{ detail.email || '—' }}</span></div>
            <div><span class="hms-kv-key">Phone</span><span>{{ detail.phone || '—' }}</span></div>
            <div v-if="detail.staff_first"><span class="hms-kv-key">Linked Staff</span><span>{{ detail.staff_first }} {{ detail.staff_last }} <span class="hms-muted-sm">· {{ detail.staff_position }}</span></span></div>
            <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="detail.is_active == 1 ? 'hms-badge-success' : 'hms-badge-neutral'">{{ detail.is_active == 1 ? 'Active' : 'Inactive' }}</span></span></div>
            <div><span class="hms-kv-key">Last Login</span><span>{{ detail.last_login ? formatDateTime(detail.last_login) : 'Never' }}</span></div>
            <div><span class="hms-kv-key">Created</span><span>{{ formatDateTime(detail.created_at) }}</span></div>
          </div>

          <div class="hms-section-label">RECENT ACTIVITY ({{ activity.length }})</div>
          <div v-if="activity.length" class="hms-activity-list">
            <div v-for="a in activity" :key="a.log_id" class="hms-activity-row">
              <div class="hms-activity-dot"></div>
              <div class="flex-fill">
                <div class="hms-text">{{ a.description || a.action }}</div>
                <div class="hms-muted-sm">{{ a.module || '—' }} · {{ formatDateTime(a.created_at) }}{{ a.ip_address ? ' · ' + a.ip_address : '' }}</div>
              </div>
            </div>
          </div>
          <div v-else class="hms-muted">No recent activity.</div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showDetail=false; openEditUser(detail)"><i class="mdi mdi-pencil me-1"></i>Edit</button>
            <button class="hms-btn hms-btn-warning flex-fill" @click="confirmReset(detail)"><i class="mdi mdi-key-variant me-1"></i>Reset Password</button>
          </div>
        </div>
      </div>

      <!-- ============ TEMP PASSWORD MODAL ============ -->
      <div class="hms-modal-overlay" v-if="tempPasswordInfo" @click.self="tempPasswordInfo=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-key text-warning"></i> Temporary Password</span>
            <button class="hms-modal-close" @click="tempPasswordInfo=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-shield-alert me-1"></i>
            Share this password securely with the user. It is shown <strong>once</strong> only — they must change it on first login.
          </div>
          <div class="hms-temp-pass-box">
            <code>{{ tempPasswordInfo.password }}</code>
            <button class="hms-btn hms-btn-sm hms-btn-blue" @click="copyPwd">
              <i class="mdi" :class="copied ? 'mdi-check' : 'mdi-content-copy'"></i> {{ copied ? 'Copied' : 'Copy' }}
            </button>
          </div>
          <div class="hms-muted-sm mt-2" v-if="tempPasswordInfo.username">
            For: <strong>@{{ tempPasswordInfo.username }}</strong>
          </div>
          <button class="hms-btn hms-btn-primary w-100 mt-3" @click="tempPasswordInfo=null">Done</button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-green  { color: var(--hms-success); }
.hms-stat-amber  { color: var(--hms-warning); }
.hms-stat-info   { color: var(--hms-info); }
.hms-stat-purple { color: #9b59b6; }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input:disabled { background: var(--hms-bg3); color: var(--hms-m); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 8px; margin-bottom: 8px; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-avatar { width: 36px; height: 36px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex-shrink: 0; }
.hms-avatar-lg { width: 48px; height: 48px; font-size: 16px; }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-warning { background: rgba(255,193,7,.15); color: #ca8a04; border: 1px solid rgba(255,193,7,.3); }
.hms-btn-warning:hover:not(:disabled) { background: rgba(255,193,7,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-green { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-green:hover:not(:disabled) { background: rgba(40,167,69,.25); }

/* Roles grid */
.hms-roles-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
.hms-role-card { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 8px; transition: all .15s; }
.hms-role-card:hover { border-color: var(--hms-primary); }
.hms-role-head { display: flex; align-items: center; gap: 8px; }
.hms-role-head .mdi { color: var(--hms-primary); font-size: 20px; }
.hms-role-name { color: var(--hms-h); font-weight: 700; font-size: 15px; }
.hms-role-code { color: var(--hms-s); }
.hms-role-desc { color: var(--hms-m); font-size: 13px; min-height: 36px; }
.hms-role-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-top: 8px; border-top: 1px dashed var(--hms-br); }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 460px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 160px; font-weight: 600; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-temp-pass-box { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.hms-temp-pass-box code { color: var(--hms-h); font-size: 18px; font-weight: 700; letter-spacing: 1px; }

.hms-activity-list { display: flex; flex-direction: column; gap: 8px; max-height: 320px; overflow-y: auto; }
.hms-activity-row { display: flex; gap: 10px; padding: 8px 0; border-bottom: 1px dashed var(--hms-br); }
.hms-activity-row:last-child { border-bottom: none; }
.hms-activity-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--hms-primary); margin-top: 6px; flex-shrink: 0; }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

/* Scope-of-visibility multi-checkbox grid (item 22) */
.hms-scope-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 8px; }
.hms-scope-item { display: flex; align-items: flex-start; gap: 8px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 10px 12px; cursor: pointer; transition: all .15s; }
.hms-scope-item:hover { border-color: var(--hms-primary); background: var(--hms-bg3); }
.hms-scope-item.active { border-color: var(--hms-primary); background: color-mix(in srgb, var(--hms-primary) 8%, var(--hms-bg1)); }
.hms-scope-item input[type=checkbox] { margin-top: 3px; accent-color: var(--hms-primary); }
.hms-scope-item span strong { color: var(--hms-h); font-size: 13px; }

/* RMS SetAccounts-style grouped rights picker (grid of group cards) */
.hms-modal-xl { max-width: 1040px; }
.hms-rights-actions { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px dashed var(--hms-br); }
.hms-rights-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 12px; max-height: 440px; overflow-y: auto; padding-right: 4px; }
.hms-rights-card { border: 1px solid var(--hms-br); border-radius: 10px; overflow: hidden; display: flex; flex-direction: column; background: var(--hms-bg1); }
.hms-rights-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 12px; background: var(--hms-primary); color: #fff; }
.hms-rights-card-name { font-size: 12.5px; font-weight: 700; display: flex; align-items: center; gap: 6px; min-width: 0; }
.hms-rights-card-name i { font-size: 15px; }
.hms-rights-card-badge { background: #fff; color: var(--hms-primary); border-radius: 20px; padding: 1px 8px; font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.hms-rights-card-body { padding: 8px 12px; max-height: 200px; overflow-y: auto; }
.hms-rights-selectall { display: flex; align-items: center; gap: 8px; margin: 0 0 6px; padding-bottom: 6px; border-bottom: 1px dashed var(--hms-br); cursor: pointer; color: var(--hms-primary); font-weight: 700; font-size: 12.5px; }
.hms-rights-selectall input[type=checkbox], .hms-rights-item input[type=checkbox] { accent-color: var(--hms-primary); }
.hms-rights-item { display: flex; align-items: center; gap: 8px; margin: 0; padding: 3px 0; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-rights-item.active span { color: var(--hms-primary); font-weight: 600; }
.hms-rights-summary { margin-top: 12px; padding: 10px 12px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; display: flex; align-items: center; flex-wrap: wrap; gap: 6px; color: var(--hms-h); font-size: 13px; }
.hms-rights-chip { display: inline-flex; align-items: center; gap: 4px; background: var(--hms-bg3); border: 1px solid var(--hms-br); border-radius: 20px; padding: 2px 10px; font-size: 11.5px; color: var(--hms-h); }
.hms-rights-chip i { cursor: pointer; font-size: 13px; }
.hms-rights-chip i:hover { color: var(--hms-danger); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "UsersAndRoles",
  components: { Navigation },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "users",

      users: [],
      roles: [],
      branches: [],
      staffList: [],
      rightsCatalog: [],

      search: "",
      filterBranch: "",
      filterRole: "",
      filterStatus: "",

      // User form
      showUserForm: false,
      editingUser: false,
      userFormError: "",
      userForm: this.emptyUserForm(),

      // Role form
      showRoleForm: false,
      editingRole: false,
      roleFormError: "",
      roleForm: this.emptyRoleForm(),
      scopeOptions: [
        { value: 'national',   label: 'National',   hint: 'MoH / DHA reviewers — sees every facility country-wide' },
        { value: 'county',     label: 'County',     hint: 'CEC or CHRIO — sees all facilities in their county' },
        { value: 'sub_county', label: 'Sub-county', hint: 'Sub-county Health Manager — sub-county roll-up' },
        { value: 'facility',   label: 'Facility',   hint: 'Medical Superintendent / Facility Admin — this facility only' },
        { value: 'department', label: 'Department', hint: 'HoD — their unit or clinic only' },
        { value: 'self',       label: 'Self',       hint: 'Own records only — patients, students, individual staff' }
      ],
      // Scope dropdowns
      counties: [],
      subCounties: [],

      // Detail
      showDetail: false,
      detail: null,
      activity: [],

      // Temp password modal
      tempPasswordInfo: null,
      copied: false,

      myUserId: null,

      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    activeCount()        { return this.users.filter(u => u.is_active == 1).length; },
    inactiveCount()      { return this.users.filter(u => u.is_active != 1).length; },
    mustChangeCount()    { return this.users.filter(u => u.must_change_password == 1).length; },
    selectedRightObjects() {
      var sel = this.roleForm.rights || [];
      var out = [];
      this.rightsCatalog.forEach(function(g){
        g.rights.forEach(function(rt){ if (sel.indexOf(Number(rt.right_id)) !== -1) out.push(rt); });
      });
      return out;
    },
    filteredUsers() {
      var list = this.users;
      if (this.filterBranch) list = list.filter(u => String(u.branch_id) === String(this.filterBranch));
      if (this.filterRole)   list = list.filter(u => String(u.role_id)   === String(this.filterRole));
      if (this.filterStatus === 'active')   list = list.filter(u => u.is_active == 1);
      if (this.filterStatus === 'inactive') list = list.filter(u => u.is_active != 1);
      var q = (this.search || "").trim().toLowerCase();
      if (q) {
        list = list.filter(u =>
          ((u.first_name||"") + " " + (u.last_name||"") + " " + (u.username||"") + " " + (u.email||"") + " " + (u.phone||""))
            .toLowerCase().indexOf(q) >= 0);
      }
      return list;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.myUserId = sessionStorage.getItem("userId");
    this.loadAll();
    this.loadCounties();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyUserForm() {
      return {
        user_id: null, first_name: "", last_name: "", email: "", phone: "",
        username: "", role_id: "", branch_id: "", staff_id: "", is_active: 1
      };
    },
    emptyRoleForm() { return { role_id: null, role_name: "", role_code: "", description: "", view_levels: ["facility"], scope_county_id: null, scope_sub_county_id: null, rights: [] }; },
    scopeHas(v) { return (this.roleForm.view_levels || []).indexOf(v) !== -1; },
    async loadCounties() {
      try {
        var r = await executeJson({ function: "listCounties" }, hms);
        if (r.data && r.data.success) this.counties = r.data.data.counties || [];
      } catch(e) { /* offline ok */ }
    },

    initials(u) {
      var f = (u.first_name||"").trim().charAt(0);
      var l = (u.last_name||"").trim().charAt(0);
      return ((f + l).toUpperCase()) || (u.username || "?").charAt(0).toUpperCase();
    },
    fullName(u) { return ((u.first_name||"") + " " + (u.last_name||"")).trim(); },
    formatDateTime(s) {
      if (!s) return "—";
      var d = new Date(s.replace ? s.replace(" ", "T") : s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString();
    },

    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadAll() {
      this.loading = true;
      try {
        var res = await Promise.all([
          executeJson({ function: "getUsers" }, hms),
          executeJson({ function: "getRoles" }, hms),
          executeJson({ function: "getBranches" }, hms),
          executeJson({ function: "getStaff" }, hms),
          executeJson({ function: "getRightsCatalog" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.users    = (res[0].data.data && res[0].data.data.Users)    || [];
        if (res[1].data && res[1].data.success) this.roles    = (res[1].data.data && res[1].data.data.Roles)    || [];
        if (res[2].data && res[2].data.success) this.branches = (res[2].data.data && res[2].data.data.Branches) || [];
        if (res[3].data && res[3].data.success) this.staffList= (res[3].data.data && res[3].data.data.Staff)    || [];
        if (res[4].data && res[4].data.success) this.rightsCatalog = (res[4].data.data && res[4].data.data.groups) || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    /* ---- Users ---- */
    openAddUser() {
      this.editingUser = false;
      this.userForm = this.emptyUserForm();
      this.userFormError = "";
      this.showUserForm = true;
    },
    openEditUser(u) {
      this.editingUser = true;
      this.userForm = Object.assign(this.emptyUserForm(), {
        user_id:    u.user_id,
        first_name: u.first_name || "",
        last_name:  u.last_name  || "",
        email:      u.email || "",
        phone:      u.phone || "",
        username:   u.username || "",
        role_id:    u.role_id || "",
        branch_id:  u.branch_id || "",
        staff_id:   u.staff_id || "",
        is_active:  u.is_active == 1 ? 1 : 0
      });
      this.userFormError = "";
      this.showUserForm  = true;
    },
    async saveUser() {
      this.userFormError = "";
      var f = this.userForm;
      if (!f.first_name || !f.last_name) { this.userFormError = "First and last name are required."; return; }
      if (!this.editingUser && !f.username) { this.userFormError = "Username is required."; return; }
      if (!f.role_id || !f.branch_id) { this.userFormError = "Role and Facility are required."; return; }
      if (!this.editingUser) {
        var email = (f.email || '').trim();
        if (!email) { this.userFormError = "Email is required — login credentials are emailed to the user."; return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { this.userFormError = "Email is not valid."; return; }
      }

      this.processing = true;
      try {
        var fn = this.editingUser ? "updateUser" : "createUser";
        var r = await executeJson(Object.assign({ function: fn }, f), hms);
        if (r.data && r.data.success) {
          this.showUserForm = false;
          if (!this.editingUser && r.data.data && r.data.data.temp_password) {
            this.tempPasswordInfo = {
              password: r.data.data.temp_password,
              username: f.username,
              emailSent: !!r.data.data.email_sent,
              emailTo:   r.data.data.email_to || f.email
            };
          } else {
            this.showToast(this.editingUser ? "User updated" : "User created");
          }
          await this.loadAll();
        } else { this.userFormError = r.data.message || "Failed."; }
      } catch(e) { this.userFormError = "Connection error."; }
      this.processing = false;
    },

    async openDetail(u) {
      this.showDetail = true;
      this.detail = u;
      this.activity = [];
      try {
        var r = await executeJson({ function: "getUserDetail", user_id: u.user_id }, hms);
        if (r.data && r.data.success) {
          this.detail = r.data.data.User || u;
          this.activity = r.data.data.Activity || [];
        }
      } catch(e) {}
    },

    async confirmReset(u) {
      if (!confirm("Reset password for " + (this.fullName(u) || u.username) + "?\n\nA new temporary password will be generated and the user will be required to change it on next login.")) return;
      try {
        var r = await executeJson({ function: "resetUserPassword", user_id: u.user_id }, hms);
        if (r.data && r.data.success) {
          this.tempPasswordInfo = { password: r.data.data.temp_password, username: u.username };
          await this.loadAll();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },

    async confirmDeactivate(u) {
      if (!confirm("Deactivate " + (this.fullName(u) || u.username) + "?")) return;
      try {
        var r = await executeJson({ function: "deactivateUser", user_id: u.user_id }, hms);
        if (r.data && r.data.success) { this.showToast("User deactivated"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },
    async unlockUser(u) {
      if (!confirm("Unlock " + (this.fullName(u) || u.username) + "'s account? They'll be able to sign in immediately.")) return;
      try {
        var r = await executeJson({ function: "unlockUser", user_id: u.user_id }, hms);
        if (r.data && r.data.success) { this.showToast("Account unlocked"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },
    async activate(u) {
      try {
        var r = await executeJson({ function: "activateUser", user_id: u.user_id }, hms);
        if (r.data && r.data.success) { this.showToast("User activated"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },
    async confirmDisableMfa(u) {
      var name = this.fullName(u) || u.username;
      if (!confirm("Disable MFA for " + name + "?\n\nThey'll be able to log in with password only. Use this only when they've lost their authenticator device and burned all backup codes.")) return;
      try {
        var r = await executeJson({ function: "adminDisableUserMfa", target_user_id: u.user_id }, hms);
        if (r.data && r.data.success) { this.showToast("MFA disabled for " + name); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },

    /* ---- Roles: grouped rights picker ---- */
    selectAllRights() {
      var sel = [];
      this.rightsCatalog.forEach(function(g){
        g.rights.forEach(function(rt){ sel.push(Number(rt.right_id)); });
      });
      this.roleForm.rights = sel;
    },
    deselectAllRights() { this.roleForm.rights = []; },
    removeRight(rt) {
      this.roleForm.rights = (this.roleForm.rights || []).filter(function(id){ return id !== Number(rt.right_id); });
    },
    selectedInGroup(g) {
      var sel = this.roleForm.rights || [];
      return g.rights.filter(function(rt){ return sel.indexOf(Number(rt.right_id)) !== -1; }).length;
    },
    isGroupFull(g) {
      return g.rights.length > 0 && this.selectedInGroup(g) === g.rights.length;
    },
    toggleGroup(g) {
      var sel = (this.roleForm.rights || []).slice();
      var ids = g.rights.map(function(rt){ return Number(rt.right_id); });
      if (this.isGroupFull(g)) {
        sel = sel.filter(function(id){ return ids.indexOf(id) === -1; });
      } else {
        ids.forEach(function(id){ if (sel.indexOf(id) === -1) sel.push(id); });
      }
      this.roleForm.rights = sel;
    },

    /* ---- Roles ---- */
    openAddRole() {
      this.editingRole = false;
      this.roleForm = this.emptyRoleForm();
      this.roleFormError = "";
      this.showRoleForm = true;
    },
    openEditRole(r) {
      this.editingRole = true;
      // view_levels may come as a CSV from the API; fall back to the legacy single-column view_level.
      var lvls = [];
      if (r.view_levels) {
        lvls = String(r.view_levels).split(',').map(function(s){ return s.trim(); }).filter(Boolean);
      } else if (r.view_level) {
        lvls = [r.view_level];
      }
      if (!lvls.length) lvls = ['facility'];
      var rids = [];
      if (r.right_ids) {
        rids = String(r.right_ids).split(',').map(function(s){ return parseInt(s, 10); }).filter(function(n){ return n > 0; });
      } else if (r.allowed_modules) {
        // Legacy role saved before the rights model: map its routes back to rights.
        var routes = String(r.allowed_modules).split(',').map(function(s){ return s.trim(); });
        this.rightsCatalog.forEach(function(g){
          g.rights.forEach(function(rt){
            if (rt.route && routes.indexOf(rt.route) !== -1) rids.push(Number(rt.right_id));
          });
        });
      }
      this.roleForm = {
        role_id: r.role_id, role_name: r.role_name || "", role_code: r.role_code || "", description: r.description || "",
        view_levels: lvls,
        scope_county_id: r.scope_county_id || null,
        scope_sub_county_id: r.scope_sub_county_id || null,
        rights: rids
      };
      this.roleFormError = "";
      this.showRoleForm = true;
    },
    async saveRole() {
      this.roleFormError = "";
      var f = this.roleForm;
      if (!f.role_name || !f.role_code) { this.roleFormError = "Name and code are required."; return; }
      if (!/^[a-z][a-z0-9_]*$/.test(f.role_code) && !this.editingRole) {
        this.roleFormError = "Role code must be lowercase letters, digits, underscores (start with a letter)."; return;
      }
      if (!(f.view_levels && f.view_levels.length)) { this.roleFormError = "Tick at least one scope of visibility."; return; }
      this.processing = true;
      try {
        var fn = this.editingRole ? "updateRole" : "createRole";
        // Send view_levels as CSV so it survives x-www-form-urlencoded serialisation cleanly.
        // rights go as a JSON array of right_ids; the server writes role_rights and
        // derives allowed_modules from the rights' routes (login pipeline unchanged).
        var payload = Object.assign({ function: fn }, f, {
          view_levels: (f.view_levels || []).join(','),
          rights: JSON.stringify(f.rights || [])
        });
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showRoleForm = false;
          this.showToast(this.editingRole ? "Role updated" : "Role created");
          await this.loadAll();
        } else { this.roleFormError = r.data.message || "Failed."; }
      } catch(e) { this.roleFormError = "Connection error."; }
      this.processing = false;
    },
    async confirmDeleteRole(r) {
      if (!confirm('Delete role "' + r.role_name + '"?\n\nThis cannot be undone.')) return;
      try {
        var resp = await executeJson({ function: "deleteRole", role_id: r.role_id }, hms);
        if (resp.data && resp.data.success) { this.showToast("Role deleted"); await this.loadAll(); }
        else { this.showToast(resp.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },

    copyPwd() {
      if (!this.tempPasswordInfo) return;
      var self = this;
      navigator.clipboard.writeText(this.tempPasswordInfo.password).then(() => {
        self.copied = true;
        setTimeout(() => { self.copied = false; }, 1500);
      }).catch(() => { self.showToast("Copy failed — select and copy manually", true); });
    }
  }
};
</script>
